"use client"

import Form from "next/form"
import { useRouter } from "next/navigation"
import { useEffect, useId, useRef, useState } from "react"
import { Button } from "~/components/legacy/common/button"
import { Link } from "~/components/legacy/common/link"
import { ArrowRightIcon, LoaderIcon, SearchIcon, XIcon } from "~/components/ui/icons"
import { cx } from "~/lib/legacy-utils"
import { client } from "~/lib/orpc-client"
import { useTrackEvent } from "~/hooks/use-track-event"

type Suggestion = { name: string; href: string; kind: "Tool" | "Category" }
type SearchResult = { query: string; suggestions: Suggestion[]; failed?: boolean }

export function DirectorySearch({ initialQuery = "" }: { initialQuery?: string }) {
  const router = useRouter()
  const trackEvent = useTrackEvent()
  const listId = useId()
  const input = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState(initialQuery)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [result, setResult] = useState<SearchResult>()
  const normalizedQuery = query.trim().slice(0, 255)
  const suggestions = result?.query === normalizedQuery ? result.suggestions : []
  const isOpen = open && normalizedQuery.length >= 2
  const pending = isOpen && result?.query !== normalizedQuery

  useEffect(() => setQuery(initialQuery), [initialQuery])

  useEffect(() => {
    if (normalizedQuery.length < 2 || !open) return
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const data = await client.search.suggestions(
          { query: normalizedQuery },
          { signal: controller.signal },
        )
        if (controller.signal.aborted) return
        setResult({
          query: normalizedQuery,
          suggestions: [
            ...data.tools
              .toSorted((a, b) => {
                const needle = normalizedQuery.toLowerCase()
                const rank = (name: string) =>
                  name.toLowerCase() === needle
                    ? 0
                    : name.toLowerCase().startsWith(needle)
                      ? 1
                      : name.toLowerCase().includes(needle)
                        ? 2
                        : 3
                return rank(a.name) - rank(b.name)
              })
              .slice(0, 5)
              .map(tool => ({
                name: tool.name,
                href: `/tools/${tool.slug}`,
                kind: "Tool" as const,
              })),
            ...data.categories.slice(0, 3).map(category => ({
              name: category.name,
              href: `/categories/${category.slug}`,
              kind: "Category" as const,
            })),
          ],
        })
      } catch {
        if (!controller.signal.aborted)
          setResult({ query: normalizedQuery, suggestions: [], failed: true })
      }
    }, 200)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [normalizedQuery, open])

  return (
    <div
      className="relative w-full"
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false)
      }}
    >
      <Form
        action="/tools"
        role="search"
        aria-label="Search the directory"
        onSubmit={() => {
          setOpen(false)
          trackEvent("directory.search_submitted", { query_length: normalizedQuery.length })
        }}
        className="flex w-full items-center gap-2 rounded-xl border border-border bg-background p-2 focus-within:ring-2 focus-within:ring-ring"
      >
        <SearchIcon aria-hidden className="ml-2 size-5 shrink-0 text-muted-foreground" />
        <label htmlFor={`${listId}-input`} className="sr-only">
          Search tools
        </label>
        <input
          ref={input}
          id={`${listId}-input`}
          name="q"
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listId : undefined}
          aria-activedescendant={
            isOpen && active >= 0 && suggestions[active] ? `${listId}-${active}` : undefined
          }
          autoComplete="off"
          maxLength={255}
          value={query}
          placeholder="Search tools, APIs or libraries…"
          className="min-w-0 flex-1 bg-transparent py-2 text-sm outline-none"
          onFocus={() => setOpen(true)}
          onChange={event => {
            setQuery(event.target.value)
            setOpen(true)
            setActive(-1)
          }}
          onKeyDown={event => {
            if (event.key === "Escape") {
              setOpen(false)
              setActive(-1)
            }
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault()
              setOpen(true)
              if (suggestions.length)
                setActive(index =>
                  event.key === "ArrowDown"
                    ? (index + 1) % suggestions.length
                    : (index <= 0 ? suggestions.length : index) - 1,
                )
            }
            if (event.key === "Enter" && isOpen && active >= 0 && suggestions[active]) {
              event.preventDefault()
              setOpen(false)
              router.push(suggestions[active].href)
              trackEvent("directory.suggestion_selected", { kind: suggestions[active].kind })
            }
          }}
        />
        {query && (
          <button
            type="button"
            aria-label="Clear search"
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => {
              setQuery("")
              setActive(-1)
              setResult(undefined)
              input.current?.focus()
            }}
          >
            <XIcon aria-hidden className="size-4" />
          </button>
        )}
        <Button type="submit" size="lg" className="shrink-0">
          Search
        </Button>
      </Form>

      {isOpen && (
        <div className="absolute inset-x-0 top-full z-30 mt-2 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg">
          <div
            id={listId}
            role="listbox"
            aria-label="Search suggestions"
            className="max-h-80 overflow-y-auto p-1.5"
          >
            {suggestions.map((suggestion, index) => (
              <div key={suggestion.href} role="presentation">
                <Link
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={active === index}
                  tabIndex={-1}
                  href={suggestion.href}
                  className={cx(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-accent",
                    active === index && "bg-accent",
                  )}
                  onPointerDown={event => event.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => setOpen(false)}
                >
                  <span className="min-w-0 flex-1 truncate font-medium">{suggestion.name}</span>
                  <span className="text-xs text-muted-foreground">{suggestion.kind}</span>
                </Link>
              </div>
            ))}
          </div>
          {pending && (
            <p
              role="status"
              className="flex items-center gap-2 px-4 pb-3 text-sm text-muted-foreground"
            >
              <LoaderIcon aria-hidden className="size-4 animate-spin" /> Searching…
            </p>
          )}
          {!pending && !suggestions.length && (
            <p role="status" className="px-4 pb-3 text-sm text-muted-foreground">
              {result?.failed
                ? "Suggestions unavailable. Press Enter to search."
                : "No quick matches. Press Enter to search all tools."}
            </p>
          )}
          <Link
            href={`/tools?q=${encodeURIComponent(normalizedQuery)}`}
            className="flex items-center justify-between gap-2 border-t border-border px-4 py-3 text-sm hover:bg-accent"
            onClick={() => setOpen(false)}
          >
            <span>See all results for “{normalizedQuery}”</span>
            <ArrowRightIcon aria-hidden className="size-4 shrink-0" />
          </Link>
        </div>
      )}
    </div>
  )
}
